// PROTOTYPE: This file was scaffolded quickly and is pending a rewrite. Do not use as a model for other parts of the codebase.
import { useCallback, useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import { R } from '@/modules/api/actions'
import { subscribeAICreateSessionEvents } from '@/modules/api/events'
import type {
    AdPlatform,
    AICreatePreselections,
    MetaAd,
    GetAICreateSessionResponse,
} from '@storefronts/common-types'
import { useAIChatContext } from './AIChatContext'
import {
    shouldArmRunTimeout,
    shouldRecoverStoredSnapshot,
    snapshotRecovery,
} from './aiCreateSessionRecovery'
import {
    streamGateTransitions,
    useAiCreateRunSignals,
} from './AiCreateRunSignals'

const MAX_POLL_DURATION = 10 * 60 * 1000
// EventSource closes permanently on non-200 (e.g. gateway 502/503), so we
// resubscribe on this cadence until timeout.
const SSE_RECONNECT_DELAY_MS = 3000
// Long enough to read the final node card before the editor takes over.
const REDIRECT_DELAY_MS = 1500

/**
 * Snapshot SSE plus the run timeout. A live run fails and redirects on AG-UI
 * rising edges. Those events are deleted with the run log, so a refresh —
 * when nothing is replayed — recovers the same outcomes from the snapshot.
 * The timeout starts with the subscription, before RUN_STARTED, and stays off
 * once the snapshot is already terminal.
 */
export function AiCreateSessionFeed(): null {
    const router = useRouter()
    const { state, dispatch } = useAIChatContext()
    const signals = useAiCreateRunSignals()
    const sessionId = state.sessionId

    const unsubscribeRef = useRef<(() => void) | null>(null)
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    )
    // Non-null while the lifecycle subscription is live — cleared by
    // stopPolling so a scheduled reconnect after a transient close won't fire.
    const activeSessionIdRef = useRef<string | null>(null)
    const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    )
    const prevResponseRef = useRef<GetAICreateSessionResponse | null>(null)
    const prevSignalsRef = useRef(signals)
    const signalsRef = useRef(signals)

    const stopPolling = useCallback(() => {
        activeSessionIdRef.current = null
        if (unsubscribeRef.current) {
            unsubscribeRef.current()
            unsubscribeRef.current = null
        }
        if (timeoutRef.current) clearTimeout(timeoutRef.current)
        timeoutRef.current = null
        if (reconnectTimeoutRef.current)
            clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
    }, [])

    const clearRunTimeout = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
        }
    }, [])

    const failRun = useCallback(
        (message: string) => {
            stopPolling()
            dispatch({ type: 'SET_ERROR', error: message })
        },
        [dispatch, stopPolling],
    )

    const completePrimary = useCallback(() => {
        clearRunTimeout()
        dispatch({ type: 'SET_COMPLETE' })
        const hash = prevResponseRef.current?.finalLandingPageHash
        // The tool result and the snapshot are different streams. Unsubscribing
        // before the hash arrives means this feed never sees it.
        if (!hash || redirectTimeoutRef.current) {
            return
        }
        stopPolling()
        redirectTimeoutRef.current = setTimeout(() => {
            void router.push(`/landingpages/${hash}?tab=0`)
        }, REDIRECT_DELAY_MS)
    }, [clearRunTimeout, dispatch, router, stopPolling])

    // A page hash exists long before the run finishes, so never redirect here:
    // the editor only opens once the page is ACTIVE.
    const handleRunTimeout = useCallback(() => {
        stopPolling()
        dispatch({
            type: 'SET_ERROR',
            error: 'AI content generation is taking longer than expected. You can check back in the Storefronts list.',
        })
    }, [dispatch, stopPolling])

    const armRunTimeout = useCallback(() => {
        if (timeoutRef.current || !activeSessionIdRef.current) {
            return
        }
        timeoutRef.current = setTimeout(handleRunTimeout, MAX_POLL_DURATION)
    }, [handleRunTimeout])

    const applySnapshotRecovery = useCallback(
        (response: GetAICreateSessionResponse) => {
            switch (snapshotRecovery(response)) {
                case 'fail':
                    failRun(response.error ?? 'The build failed')
                    return
                case 'complete':
                    completePrimary()
                    return
                default:
                    if (shouldArmRunTimeout(signalsRef.current, response)) {
                        armRunTimeout()
                    }
            }
        },
        [armRunTimeout, completePrimary, failRun],
    )

    const handlePollResponse = useCallback(
        (response: GetAICreateSessionResponse) => {
            prevResponseRef.current = response
            dispatch({ type: 'SET_SNAPSHOT', snapshot: response })
            if (signalsRef.current.primaryPipelineFinished) {
                completePrimary()
                return
            }
            // A live run owns its outcome. The snapshot stream closes on
            // `done`, so this payload is not delivered again — the idle
            // transition below recovers from prevResponseRef.
            if (signalsRef.current.runActive) {
                if (shouldArmRunTimeout(signalsRef.current, response)) {
                    armRunTimeout()
                }
                return
            }
            applySnapshotRecovery(response)
        },
        [applySnapshotRecovery, armRunTimeout, completePrimary, dispatch],
    )

    const beginPolling = useCallback(
        (id: string) => {
            stopPolling()
            activeSessionIdRef.current = id

            const connect = () => {
                if (activeSessionIdRef.current !== id) return

                unsubscribeRef.current = subscribeAICreateSessionEvents(
                    id,
                    handlePollResponse,
                    (err) => {
                        console.error('AI create SSE error:', err)
                        const unsub = unsubscribeRef.current
                        unsubscribeRef.current = null
                        unsub?.()
                        if (activeSessionIdRef.current !== id) return
                        reconnectTimeoutRef.current = setTimeout(
                            connect,
                            SSE_RECONNECT_DELAY_MS,
                        )
                    },
                )
            }

            connect()
            // Restarting the subscription clears the clock. Put it back only
            // when this session is still an open run.
            if (
                shouldArmRunTimeout(signalsRef.current, prevResponseRef.current)
            ) {
                armRunTimeout()
            }
        },
        [armRunTimeout, handlePollResponse, stopPolling],
    )

    useEffect(
        () => () => {
            stopPolling()
            if (redirectTimeoutRef.current)
                clearTimeout(redirectTimeoutRef.current)
        },
        [stopPolling],
    )

    // Effect-driven so the subscription survives remounts (dev fast-refresh,
    // navigation): a dead lifecycle stream means no terminal redirect even
    // though the AG-UI stream keeps narrating.
    useEffect(() => {
        if (!sessionId) {
            return
        }
        beginPolling(sessionId)
        return stopPolling
    }, [sessionId, beginPolling, stopPolling])

    useEffect(() => {
        signalsRef.current = signals
        const prev = prevSignalsRef.current
        prevSignalsRef.current = signals
        const gates = streamGateTransitions(prev, signals)
        for (const gate of gates) {
            switch (gate) {
                case 'runFailed':
                    failRun(signals.runErrorMessage ?? 'The build failed')
                    break
                case 'primaryFinished':
                    completePrimary()
                    break
            }
        }
        // RUN_FINISHED only clears runActive, and the snapshot stream has
        // already closed. A reset to empty signals, or a finish after the
        // stream already settled, must not replay the stored snapshot.
        if (
            shouldRecoverStoredSnapshot(prev, signals, gates) &&
            prevResponseRef.current
        ) {
            applySnapshotRecovery(prevResponseRef.current)
            return
        }
        if (shouldArmRunTimeout(signals, prevResponseRef.current)) {
            armRunTimeout()
        }
    }, [
        signals,
        applySnapshotRecovery,
        armRunTimeout,
        completePrimary,
        failRun,
    ])

    return null
}

export function useAIChatOrchestrator() {
    const { dispatch } = useAIChatContext()

    const startFlow = useCallback(
        async (
            ad: MetaAd,
            platform?: AdPlatform,
            variantCount?: number,
            userInput?: string,
            preselections?: AICreatePreselections,
            referenceUrl?: string,
        ) => {
            dispatch({ type: 'SET_AD', ad })
            dispatch({ type: 'CLEAR_ERROR' })

            try {
                const { sessionId } = await R.createAICreateSessionFromMetaAd(
                    ad.id,
                    platform,
                    variantCount,
                    userInput,
                    preselections,
                    referenceUrl,
                )
                dispatch({ type: 'SET_SESSION_ID', sessionId })
            } catch (err) {
                console.error('Start flow error:', err)
                dispatch({
                    type: 'SET_ERROR',
                    error: err instanceof Error ? err.message : String(err),
                })
            }
        },
        [dispatch],
    )

    const startManualFlow = useCallback(
        async (
            assets: { mediaType: 'IMAGE' | 'VIDEO'; creativeUrl: string }[],
            adCreativeBody?: string,
            adDeepLinkUrl?: string,
            adName?: string,
            platform?: AdPlatform,
            variantCount?: number,
            userInput?: string,
            preselections?: AICreatePreselections,
            referenceUrl?: string,
        ) => {
            dispatch({ type: 'CLEAR_ERROR' })

            try {
                const { sessionId } = await R.createAICreateSessionManual(
                    assets,
                    adCreativeBody,
                    adDeepLinkUrl,
                    adName,
                    platform,
                    variantCount,
                    userInput,
                    preselections,
                    referenceUrl,
                )
                dispatch({ type: 'SET_SESSION_ID', sessionId })
            } catch (err) {
                console.error('Start manual flow error:', err)
                dispatch({
                    type: 'SET_ERROR',
                    error: err instanceof Error ? err.message : String(err),
                })
            }
        },
        [dispatch],
    )

    const startSearchQueryFlow = useCallback(
        async (
            searchQuery: string,
            currentLandingPageUrl?: string,
            platform?: AdPlatform,
            variantCount?: number,
            userInput?: string,
            preselections?: AICreatePreselections,
            referenceUrl?: string,
        ) => {
            dispatch({ type: 'CLEAR_ERROR' })

            try {
                const { sessionId } =
                    await R.createAICreateSessionFromSearchQuery(
                        searchQuery,
                        currentLandingPageUrl,
                        platform,
                        variantCount,
                        userInput,
                        preselections,
                        referenceUrl,
                    )
                dispatch({ type: 'SET_SESSION_ID', sessionId })
            } catch (err) {
                console.error('Start search query flow error:', err)
                dispatch({
                    type: 'SET_ERROR',
                    error: err instanceof Error ? err.message : String(err),
                })
            }
        },
        [dispatch],
    )

    const startFlowWithSession = useCallback(
        async (newSessionId: string) => {
            dispatch({ type: 'CLEAR_ERROR' })
            dispatch({ type: 'SET_SESSION_ID', sessionId: newSessionId })
        },
        [dispatch],
    )

    return {
        startFlow,
        startManualFlow,
        startSearchQueryFlow,
        startFlowWithSession,
    }
}
